import React, { useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    Stack,
    Select,
    MenuItem,
    Divider,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {
    BarChart as DokIcon,
    ShowChart as ChartIcon,
    Support as SupportIcon,
    LockOpen as PublicIcon,
    Description as ReportIcon,
    Analytics as AnalyticsIcon
} from "@mui/icons-material";

import DokDistribution from "./DokDistribution";


const StudentAnalytics = () => {
    const [activeTab, setActiveTab] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    const tabs = [
        {
            index: 0,
            label: "DOK Distribution",
            description: "Visualize DOK level progress by skill",
            icon: <DokIcon />,
            component: <DokDistribution />
        },
        {
            index: 1,
            label: "Charts (Coming Soon)",
            description: "Analyze student growth and proficiency (Upcoming)",
            icon: <ChartIcon />,
            component: (
                <Typography color="text.secondary">
                    📈 Charts and analytics are under development.
                </Typography>
            )
        },
        {
            index: 2,
            label: "Support Overview (Coming Soon)",
            description: "Insights into support and interventions (Upcoming)",
            icon: <SupportIcon />,
            component: (
                <Typography color="text.secondary">
                    🧠 Support and intervention analysis is coming soon.
                </Typography>
            )
        },


    ];

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.default,
                minHeight: "100vh",
                p: { xs: 2, sm: 3, md: 4 },
                maxWidth: "100%",
                mx: "auto"
            }}
        >
            {/* Header */}
            <Box sx={{ mb: { xs: 3, sm: 4, md: 5 }, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: { xs: 1.5, sm: 2, md: 2.5 }, mb: 2 }}>
                    <AnalyticsIcon sx={{ 
                        fontSize: { xs: 20, sm: 24, md: 28, lg: 32 }, 
                        color: 'primary.main' 
                    }} />
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{
                            color: theme.palette.primary.dark,
                            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.35rem', lg: '1.45rem' },
                            textAlign: "left",
                            lineHeight: 1.3
                        }}
                    >
                        Student Analytics Dashboard
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, ml: { xs: 5, sm: 6 } }}>
                    Explore insights on progress, proficiency, access, and more.
                </Typography>
            </Box>

            {/* Top Menu */}
            {isMobile ? (
                <Stack spacing={2}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color: theme.palette.primary.dark,
                            textAlign: "center",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" },
                            mb: 1
                        }}
                    >
                        Select Section
                    </Typography>
                    <Select
                        fullWidth
                        value={activeTab !== null ? activeTab : ''}
                        onChange={(e) => setActiveTab(e.target.value)}
                        displayEmpty
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            borderRadius: 2,
                            mb: 4,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 600,
                            fontSize: '1rem',
                            '& .MuiSelect-select': { py: 2, px: 2.5 },
                            '& .MuiMenuItem-root': { fontSize: '1rem' }
                        }}
                    >
                        <MenuItem value="" disabled>Select a section</MenuItem>
                        {tabs.map((tab) => (
                            <MenuItem key={tab.index} value={tab.index}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ fontSize: '1.25rem' }}>{tab.icon}</Box>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>{tab.label}</Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
            ) : (
                <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 3, sm: 4, md: 5 }, maxWidth: '900px', mx: 'auto' }}>
                    {tabs.map((tab) => (
                        <Grid item xs={12} sm={6} md={4} key={tab.index}>
                            <Card
                                elevation={activeTab === tab.index ? 6 : 3}
                                onClick={() => setActiveTab(tab.index)}
                                sx={{
                                    height: '100%',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    border: activeTab === tab.index ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                                    borderColor: activeTab === tab.index ? 'primary.main' : 'divider',
                                    backgroundColor: activeTab === tab.index ? 'primary.main' : 'background.paper',
                                    color: activeTab === tab.index ? 'primary.contrastText' : 'inherit',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4
                                    }
                                }}
                            >
                                <CardContent sx={{ 
                                    textAlign: 'center', 
                                    p: { xs: 1.5, sm: 2, md: 2.5 },
                                    '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1, sm: 1.5 } }}>
                                        <Box sx={{
                                            p: { xs: 1, sm: 1.5 },
                                            borderRadius: '50%',
                                            backgroundColor: activeTab === tab.index ? 'primary.contrastText' : 'grey.100',
                                            color: activeTab === tab.index ? 'primary.main' : 'grey.600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                                        }}>
                                            {tab.icon}
                                        </Box>
                                    </Box>
                                    <Typography 
                                        variant="subtitle1" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }, 
                                            color: activeTab === tab.index ? 'primary.contrastText' : 'inherit',
                                            mb: { xs: 0.5, sm: 0.75 },
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {tab.label}
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }, 
                                            color: activeTab === tab.index ? 'primary.contrastText' : 'text.secondary',
                                            opacity: 0.85,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {tab.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Divider */}
            {activeTab !== null && (
                <Divider sx={{ my: 5, borderColor: theme.palette.divider }} />
            )}

            {/* Content Renderer */}
            {activeTab !== null && (
                <Paper elevation={4} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>
                    {tabs[activeTab].component}
                </Paper>
            )}
        </Box>
    );
};

export default StudentAnalytics;
